import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Layers, Home } from "lucide-react";
import { usePageContent } from "@/hooks/usePageContent";
import heroImage1600 from "@/assets/optimized/hero-villa-1600.jpg";
import heroImage960 from "@/assets/optimized/hero-villa-960.jpg";
import maHeroImage from "@/assets/optimized/ma-neighborhood-1600.jpg";
import maHeroImage960 from "@/assets/optimized/ma-neighborhood-960.jpg";

const ChevronDown = () => (
  <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const heroImages = [
  {
    src: heroImage1600,
    srcSet: `${heroImage960} 960w, ${heroImage1600} 1600w`,
    width: 1600,
    height: 900,
  },
  {
    src: maHeroImage,
    srcSet: `${maHeroImage960} 960w, ${maHeroImage} 1600w`,
    width: 1600,
    height: 893,
  },
];
const SHOW_HERO_SEARCH = false;

const HeroSearchModule = () => (
  <div className="max-w-4xl w-full">
    <div className="rounded-sm border border-white/10 bg-white/[0.06] p-4 shadow-[0_16px_44px_-18px_rgba(0,0,0,0.5)] backdrop-blur-2xl sm:p-5 md:p-6">
      <div className="mb-4">
        <label htmlFor="hero-search" className="font-body text-xs uppercase tracking-[0.12em] text-white/70 font-medium mb-2.5 block">
          Where are you looking?
        </label>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-accent" aria-hidden="true" />
          <input
            id="hero-search"
            type="text"
            placeholder="Search by city, neighborhood, or address..."
            className="w-full bg-white/[0.04] border border-white/[0.06] pl-11 pr-5 py-3.5 font-body text-[15px] text-cream placeholder:text-white/25 focus:border-accent/30 focus:ring-2 focus:ring-accent/10 transition-all rounded-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {[
          { id: "hero-location", label: "Location", icon: MapPin, options: ["All Locations", "Boston, MA", "Cambridge, MA", "Cape Cod, MA", "Punta Cana, DR", "Santo Domingo, DR"] },
          { id: "hero-category", label: "Category", icon: Layers, options: ["All Categories", "Residential", "Investment", "Luxury", "New Development", "Commercial"] },
          { id: "hero-type", label: "Property Type", icon: Home, options: ["All Types", "Single Family", "Condo", "Townhouse", "Villa", "Penthouse", "Land"] },
        ].map((filter) => (
          <div key={filter.id}>
            <label htmlFor={filter.id} className="font-body text-[10px] uppercase tracking-[0.1em] text-white/55 font-medium mb-2 block">
              {filter.label}
            </label>
            <div className="relative">
              <filter.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" aria-hidden="true" />
              <select
                id={filter.id}
                className="w-full appearance-none bg-white/[0.04] border border-white/[0.06] pl-8 pr-7 py-2.5 font-body text-sm text-cream/90 focus:ring-2 focus:ring-accent/15 cursor-pointer transition-all rounded-sm"
              >
                {filter.options.map((o) => (
                  <option key={o} className="text-foreground bg-card">{o}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDown />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button variant="gold" size="default" className="w-full gap-2 h-[52px] text-[15px] shadow-[0_2px_12px_-2px_rgba(191,164,105,0.25)] hover:shadow-[0_4px_20px_-2px_rgba(191,164,105,0.35)] hover:-translate-y-px transition-all duration-300" aria-label="Search properties">
        <Search className="w-4 h-4" aria-hidden="true" />
        Search Properties
      </Button>
    </div>
  </div>
);

const HeroSection = () => {
  const [revealed, setRevealed] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const [activeHeroImageIndex, setActiveHeroImageIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const { getContent } = usePageContent({ pageKey: "home" });

  const heroTitle = getContent("hero", "title", "Hadassah Homes: Real Estate & Design");
  const heroSubtitle = getContent(
    "hero",
    "subtitle",
    "Buy, sell, and invest with clarity and confidence. Hadassah Homes combines real estate expertise, interior design, and property preparation to help you move smarter and maximize every opportunity.",
  );
  const primaryCtaLabel = getContent("hero", "primary_cta_label", "Book a Consultation");
  const primaryCtaUrl = getContent("hero", "primary_cta_url", "/consultation");
  const secondaryCtaLabel = getContent(
    "hero",
    "secondary_cta_label",
    "Explore Dominican Republic Properties",
  );
  const secondaryCtaUrl = getContent(
    "hero",
    "secondary_cta_url",
    "/dominican-republic-investing/properties",
  );

  useEffect(() => {
    const hasRevealed = sessionStorage.getItem("curtainRevealed");
    if (hasRevealed) {
      setRevealed(true);
    } else {
      sessionStorage.setItem("curtainRevealed", "true");
      const timer = setTimeout(() => setRevealed(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const onScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      setOffsetY(window.scrollY);
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onScroll]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const interval = window.setInterval(() => {
      setActiveHeroImageIndex((current) => (current + 1) % heroImages.length);
    }, 18000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden flex items-center" aria-label="Hero">
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translate3d(0, ${offsetY * 0.3}px, 0)` }}
      >
        <div className="absolute inset-0 z-0 overflow-hidden">
          {heroImages.map((image, index) => {
            const isActive = index === activeHeroImageIndex;

            return (
              <img
                key={image.src}
                src={image.src}
                srcSet={image.srcSet}
                sizes="100vw"
                alt=""
                aria-hidden="true"
                className={[
                  "absolute inset-0 h-full w-full object-cover object-center scale-[1.02]",
                  "transition-opacity duration-[3000ms] ease-in-out will-change-opacity",
                  isActive ? "opacity-100" : "opacity-0",
                ].join(" ")}
                width={image.width}
                height={image.height}
                loading={index === 0 ? "eager" : "lazy"}
                {...({ fetchpriority: index === 0 ? "high" : "auto" } as any)}
                decoding={index === 0 ? "sync" : "async"}
              />
            );
          })}
        </div>
      </div>

      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-rich-black/[0.72] via-rich-black/[0.60] to-rich-black/[0.82]" />

      <div className="relative z-10 mx-auto flex w-full max-w-[1520px] flex-col items-center px-5 py-36 md:px-12 md:py-48 lg:px-20">
        {/* Headline group */}
        <div className={`max-w-5xl text-center ${SHOW_HERO_SEARCH ? "mb-6 md:mb-8" : "mb-10 md:mb-12"}`}>
          <p className="font-body text-[10px] uppercase tracking-[0.4em] text-accent/90 mb-3 md:mb-4">
            Massachusetts & Dominican Republic
          </p>
          <h1
            className="font-headline text-[3.25rem] text-cream leading-[0.89] tracking-[-0.02em] sm:text-[4.25rem] md:text-[5.25rem] lg:text-[6.25rem]"
            style={{ textShadow: "0 10px 30px rgba(0,0,0,0.34)" }}
          >
            {heroTitle}
          </h1>
          <p className="mt-8 md:mt-11 font-body text-cream/75 text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-[1.75]">
            {heroSubtitle}
          </p>
        </div>

        {SHOW_HERO_SEARCH ? (
          <HeroSearchModule />
        ) : (
          <div className="flex w-full max-w-3xl flex-col items-center text-center">
            <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                asChild
                variant="gold"
                size="default"
                className="w-full sm:w-auto px-8 h-[52px] text-[15px] shadow-[0_2px_12px_-2px_rgba(191,164,105,0.25)] hover:shadow-[0_4px_20px_-2px_rgba(191,164,105,0.35)] hover:-translate-y-px transition-all duration-300"
              >
                <a href={primaryCtaUrl}>{primaryCtaLabel}</a>
              </Button>
              <a
                href={secondaryCtaUrl}
                className="inline-flex h-[52px] w-full items-center justify-center border border-white/24 bg-white/[0.03] px-8 font-body text-[15px] uppercase tracking-[0.12em] text-cream transition-colors hover:border-accent/60 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-rich-black sm:w-auto"
              >
                {secondaryCtaLabel}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
